import React from "react";
import { observer } from "mobx-react-lite";
import { editorStore } from "../../store/editorStore";
import { floorplannerStore } from "../../store/floorplannerStore";
import PropertyPanelHeader from "./PropertyPanel/PropertyPanelHeader";
import { usePropertyPanelController } from "./PropertyPanel/usePropertyPanelController";
import LightingSection from "./PropertyPanel/sections/LightingSection";
import WallSection from "./PropertyPanel/sections/WallSection";
import SingleLineSection from "./PropertyPanel/sections/SingleLineSection";
import RulerLineSection from "./PropertyPanel/sections/RulerLineSection";
import Ruler3DSection from "./PropertyPanel/sections/Ruler3DSection";
import DoorSection from "./PropertyPanel/sections/DoorSection";
import DoubleDoorSection from "./PropertyPanel/sections/DoubleDoorSection";
import WindowSection from "./PropertyPanel/sections/WindowSection";
import VoidSection from "./PropertyPanel/sections/VoidSection";
import StairsSection from "./PropertyPanel/sections/StairsSection";
import ShapeSection from "./PropertyPanel/sections/ShapeSection";
import SvgSection from "./PropertyPanel/sections/SvgSection";
import AreaFillSection from "./PropertyPanel/sections/AreaFillSection";
import LineAppearanceSection from "./PropertyPanel/sections/LineAppearanceSection";
import FillSection from "./PropertyPanel/sections/FillSection";
import BackdropImageSection from "./PropertyPanel/sections/BackdropImageSection";
import BackgroundImagesSection from "./PropertyPanel/sections/BackgroundImagesSection";
import ProductAndMaterialsSection from "./PropertyPanel/sections/ProductAndMaterialsSection";
import ProductListSection from "./PropertyPanel/sections/ProductListSection";
import SmartTableSection from "./PropertyPanel/sections/SmartTableSection";
import BookShelfSection from "./PropertyPanel/sections/BookShelfSection";

const PropertyPanel: React.FC = observer(() => {
  const controller = usePropertyPanelController();
  const { grouped } = controller;
  const hasSelection = editorStore.selections.length > 0;

  return (
    <>
      <PropertyPanelHeader
        optionMenuOpen={controller.optionMenuOpen}
        setOptionMenuOpen={controller.setOptionMenuOpen}
        optionMenuRef={controller.optionMenuRef}
        floorplans={controller.floorplans}
        isDeletePopupOpen={controller.isDeletePopupOpen}
        setIsDeletePopupOpen={controller.setIsDeletePopupOpen}
      />
      {!editorStore.minimized && (
        <div
          className={
            floorplannerStore.lockedByOther
              ? "pointer-events-none opacity-70"
              : undefined
          }
        >
          {!hasSelection && <ProductListSection />}
          {editorStore.view3D && !hasSelection && (
            <LightingSection
              open={controller.lightingPropsOpen}
              onToggle={() =>
                controller.setLightingPropsOpen(!controller.lightingPropsOpen)
              }
            />
          )}
          <WallSection
            selectedWalls={grouped.selectedWalls}
            displayedWalls={grouped.displayedWalls}
            commonWallLength={grouped.commonWallLength}
            commonWallWidth={grouped.commonWallWidth}
            commonWallHeight={grouped.commonWallHeight}
            wallLength={controller.wallLength}
            setWallLength={controller.setWallLength}
            localValues={controller.localValues}
            setLocalValues={controller.setLocalValues}
            updateGlobalState={controller.updateGlobalState}
            updateWallAttachmentWidths={controller.updateWallAttachmentWidths}
          />
          <SingleLineSection
            selectedSingleLines={grouped.selectedSingleLines}
            displayedSingleLines={grouped.displayedSingleLines}
            commonSingleLineLength={grouped.commonSingleLineLength}
            singleLineLength={controller.singleLineLength}
            setSingleLineLength={controller.setSingleLineLength}
            updateGlobalState={controller.updateGlobalState}
          />
          <RulerLineSection
            selectedRulerLines={grouped.selectedRulerLines}
            displayedRulerLines={grouped.displayedRulerLines}
            commonRulerLength={grouped.commonRulerLength}
            rulerLength={controller.rulerLength}
            setRulerLength={controller.setRulerLength}
            updateGlobalState={controller.updateGlobalState}
          />
          <Ruler3DSection
            selectedRulers3D={grouped.selectedRulers3D}
            displayedRulers3D={grouped.displayedRulers3D}
            commonRuler3DDistance={grouped.commonRuler3DDistance}
            ruler3DDistance={controller.ruler3DDistance}
            setRuler3DDistance={controller.setRuler3DDistance}
            updateGlobalState={controller.updateGlobalState}
          />
          <DoorSection
            selectedDoors={grouped.selectedDoors}
            displayedDoors={grouped.displayedDoors}
            updateGlobalState={controller.updateGlobalState}
            is3DView={editorStore.view3D}
          />
          <DoubleDoorSection
            selectedDoubleDoors={grouped.selectedDoubleDoors}
            displayedDoubleDoors={grouped.displayedDoubleDoors}
            updateGlobalState={controller.updateGlobalState}
            is3DView={editorStore.view3D}
          />
          <WindowSection
            selectedWindows={grouped.selectedWindows}
            displayedWindows={grouped.displayedWindows}
            updateGlobalState={controller.updateGlobalState}
            is3DView={editorStore.view3D}
          />
          <VoidSection
            selectedVoids={grouped.selectedVoids}
            displayedVoids={grouped.displayedVoids}
            updateGlobalState={controller.updateGlobalState}
            is3DView={editorStore.view3D}
          />
          <StairsSection
            selectedCircleStairs={grouped.selectedCircleStairs}
            displayedCircleStairs={grouped.displayedCircleStairs}
            selectedRectStairs={grouped.selectedRectStairs}
            displayedRectStairs={grouped.displayedRectStairs}
            selectedTurnStairs={grouped.selectedTurnStairs}
            displayedTurnStairs={grouped.displayedTurnStairs}
            updateGlobalState={controller.updateGlobalState}
            is3DView={editorStore.view3D}
          />
          <ShapeSection
            selectedSquares={grouped.selectedSquares}
            displayedSquares={grouped.displayedSquares}
            selectedCircles={grouped.selectedCircles}
            displayedCircles={grouped.displayedCircles}
            selectedTriangles={grouped.selectedTriangles}
            displayedTriangles={grouped.displayedTriangles}
            selectedPolygons={grouped.selectedPolygons}
            displayedPolygons={grouped.displayedPolygons}
            updateGlobalState={controller.updateGlobalState}
            is3DView={editorStore.view3D}
          />
          <SmartTableSection
            selectedSmartTables={grouped.selectedSmartTables}
            displayedSmartTables={grouped.displayedSmartTables}
            updateGlobalState={controller.updateGlobalState}
          />
          <BookShelfSection
            selectedBookShelves={grouped.selectedBookShelves}
            displayedBookShelves={grouped.displayedBookShelves}
            updateGlobalState={controller.updateGlobalState}
          />
          <SvgSection
            selectedSvgs={grouped.selectedSvgs}
            displayedSvgs={grouped.displayedSvgs}
            updateGlobalState={controller.updateGlobalState}
            is3DView={editorStore.view3D}
          />
          <AreaFillSection
            selectedAreaFillSymbols={grouped.selectedAreaFillSymbols}
            displayedAreaFillSymbols={grouped.displayedAreaFillSymbols}
          />
          <LineAppearanceSection
            grouped={grouped}
            linePropsOpen={controller.linePropsOpen}
            setLinePropsOpen={controller.setLinePropsOpen}
            lineTypeOptions={controller.lineTypeOptions}
            updateGlobalState={controller.updateGlobalState}
          />
          <FillSection
            grouped={grouped}
            fillPropsOpen={controller.fillPropsOpen}
            setFillPropsOpen={controller.setFillPropsOpen}
            handleFillColorChange={controller.handleFillColorChange}
            flushPendingFillColorUpdate={controller.flushPendingFillColorUpdate}
            handleFillOpacityChange={controller.handleFillOpacityChange}
            flushPendingFillOpacityUpdate={controller.flushPendingFillOpacityUpdate}
            handleFillOpacityRestore={controller.handleFillOpacityRestore}
          />
          <BackdropImageSection
            backdropModal={controller.backdropModal}
            setBackdropModal={controller.setBackdropModal}
            deleteBackdropImage={controller.deleteBackdropImage}
          />
          <ProductAndMaterialsSection
            selectedWalls={grouped.selectedWalls}
            selectedAreaFillSymbols={grouped.selectedAreaFillSymbols}
            selectedSvgs={grouped.selectedSvgs}
            selectedSmartTables={grouped.selectedSmartTables}
            selectedBookShelves={grouped.selectedBookShelves}
            selectedSquares={grouped.selectedSquares}
            selectedCircles={grouped.selectedCircles}
            selectedTriangles={grouped.selectedTriangles}
            selectedPolygons={grouped.selectedPolygons}
            selectedCircleStairs={grouped.selectedCircleStairs}
            selectedRectStairs={grouped.selectedRectStairs}
            selectedTurnStairs={grouped.selectedTurnStairs}
            selectedDoors={grouped.selectedDoors}
            selectedDoubleDoors={grouped.selectedDoubleDoors}
          />
          <BackgroundImagesSection
            open={controller.layerPropsOpen}
            onToggle={() =>
              controller.setLayerPropsOpen(!controller.layerPropsOpen)
            }
          />
        </div>
      )}
    </>
  );
});

export default PropertyPanel;
